import {
  Children, isValidElement,
  useEffect, useId, useMemo, useRef, useState,
  ReactNode, KeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { IconChevron } from './Icon';
import { useAnchoredDropdown } from '@/shared/hooks/useAnchoredDropdown';
import { moveFocusVertical, moveFocusHorizontal } from '@/shared/keynav';
import { useFieldAria } from './FieldContext';
import { DrillAffordance, useOpenRecord, DRILL_BOX_FOCUS, DRILL_BOX_INERT_FOCUS, DRILL_TRIGGER } from './DrillAffordance';

type Opt = { value: string; label: string; disabled?: boolean };

// Keep backward-compat with <select> callers — value, onChange (receives
// e.target.value), children (<option> elements), disabled, className all
// work the same. Under the hood we render a custom combobox so clicking
// the field body just focuses it, and only clicking the arrow (or
// Enter/Space/Alt+↓/F4 on keyboard) opens the options list.
type SelectProps = {
  value?: string | number | readonly string[];
  onChange?: (e: { target: { value: string } }) => void;
  children?: ReactNode;
  invalid?: boolean;
  disabled?: boolean;
  // Read-only display: stays focusable for keyboard navigation, but
  // pressing keys / clicking the arrow won't open the dropdown and the
  // value can't change. Visually identical to disabled.
  readOnly?: boolean;
  className?: string;
  // 'warning' keeps a rose border while focused (the box otherwise turns teal).
  tone?: 'warning';
  id?: string;
  autoFocus?: boolean;
  required?: boolean;
  name?: string;
  title?: string;
  'aria-label'?: string;
  // Shorter text for the closed box (e.g. a warehouse's code where the list
  // reads "MAIN — Main Warehouse"); the box then carries the full label as
  // its title. The list keeps the full labels.
  closedLabel?: string;
  // In-box drill-in, same names and look as AsyncPicker: with a value, an
  // active icon after a divider opens `openTo` (Alt+Enter on the field does too),
  // pushing a zoom crumb labelled `fromLabel`. alwaysDrill keeps a dim, inert icon
  // while the field is empty. openLabel names the icon ("Open category").
  openTo?: string;
  alwaysDrill?: boolean;
  fromLabel?: string;
  openLabel?: string;
  // For the customer portal, whose people do not work in these forms all day and
  // may be on a phone: a click anywhere on the box opens the list, and its rows
  // are big enough for a finger.
  roomy?: boolean;
};

// Flatten any React children into a plain string. Callers often render
// option labels as JSX with multiple children, e.g.
//   <option value={id}>{code} — {name}</option>
// which React passes as an array. Naively stringifying the array inserts
// commas between entries ("BCSYSTEM,— ,BC Systems, Inc"), so we walk the
// tree and concatenate text nodes instead.
function childrenToText(children: ReactNode): string {
  if (children == null || typeof children === 'boolean') return '';
  if (typeof children === 'string' || typeof children === 'number') return String(children);
  if (Array.isArray(children)) return children.map(childrenToText).join('');
  if (isValidElement(children)) return childrenToText((children.props as { children?: ReactNode }).children);
  return '';
}

function extractOptions(children: ReactNode): Opt[] {
  const out: Opt[] = [];
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    if (child.type === 'option') {
      const p = child.props as { value?: string | number; children?: ReactNode; disabled?: boolean };
      out.push({
        value: String(p.value ?? ''),
        label: childrenToText(p.children),
        disabled: !!p.disabled,
      });
    } else if (child.type === 'optgroup') {
      out.push(...extractOptions((child.props as { children?: ReactNode }).children));
    }
  });
  return out;
}

export function Select({
  value, onChange, children, invalid, disabled, readOnly, className = '', tone,
  openTo, alwaysDrill, fromLabel, openLabel = 'Open record', closedLabel, roomy = false,
  ...rest
}: SelectProps) {
  const inert = disabled || readOnly;
  const openRecord = useOpenRecord(openTo, fromLabel);
  // Named by the surrounding field's label unless the caller named it.
  const aria = useFieldAria(rest, invalid);
  const isInvalid = aria['aria-invalid'] === true || aria['aria-invalid'] === 'true';
  const options = useMemo(() => extractOptions(children), [children]);
  const currentValue = String(value ?? '');
  const selected = options.find((o) => o.value === currentValue);

  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  // Combobox wiring: the trigger (or, on a long list, the filter field)
  // controls the listbox and points at the highlighted option, so a screen
  // reader announces each option as the arrows move.
  const listId = useId();
  const optionId = (i: number) => `${listId}opt${i}`;

  // Type-ahead (customer feedback: "make all drop-downs searchable"): lists
  // longer than the threshold get a filter input at the top of the panel.
  // Every token of the query must appear in the option label ("wid red"
  // finds "WID-123 Red Widget"). Small enum lists stay input-free.
  const FILTER_THRESHOLD = 9;
  const filterable = options.length > FILTER_THRESHOLD;
  const [query, setQuery] = useState('');
  const seedRef = useRef<string | null>(null);
  const shown = useMemo(() => {
    if (!filterable || !query.trim()) return options;
    const toks = query.toLowerCase().split(/\s+/).filter(Boolean);
    return options.filter((o) => {
      const l = o.label.toLowerCase();
      return toks.every((t) => l.includes(t));
    });
  }, [options, query, filterable]);

  // Position the options list as a fixed, portaled panel so it overflows
  // any clipping ancestor (e.g. a modal body with overflow:auto).
  const menuStyle = useAnchoredDropdown(rootRef, open && !inert, { maxHeight: 256 });

  // When opening: seed the filter (a printable key pressed on the closed
  // trigger carries into it), and highlight the current selection so
  // arrow-nav starts from where the user left off.
  useEffect(() => {
    if (!open) return;
    setQuery(seedRef.current ?? '');
    seedRef.current = null;
    const i = options.findIndex((o) => o.value === currentValue);
    setHighlight(i >= 0 ? i : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Narrowing the filter re-anchors the highlight to the first match.
  useEffect(() => { setHighlight(0); }, [query]);

  // Scroll the highlighted row into view as the user arrows through.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(`[data-idx="${highlight}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlight, open]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      // The list is portaled outside rootRef, so check it explicitly.
      if (rootRef.current?.contains(target) || listRef.current?.contains(target)) return;
      setOpen(false);
    };
    // Escape closes the list only. Capture phase on document, so it runs before
    // a Modal's Escape listener (document, bubble) and marks the key used: the
    // dialog around the select stays open (Wave 1 Escape contract).
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      setOpen(false);
      btnRef.current?.focus();
    };
    window.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  function pick(opt: Opt) {
    if (opt.disabled) return;
    onChange?.({ target: { value: opt.value } });
    setOpen(false);
    btnRef.current?.focus();
  }

  const hasDrill = !!(openTo || alwaysDrill);
  const canOpen = !!(openTo && currentValue);

  function onTriggerKeyDown(e: KeyboardEvent<HTMLButtonElement>) {
    if (!open) {
      if (e.altKey && e.key === 'Enter' && canOpen) { e.preventDefault(); openRecord(); return; }
      // Enter / Space / F4 / Alt+↓/↑ open the list. Plain arrows TRAVEL to the
      // neighbouring field — the form is keyboard-navigable like a grid, so a
      // closed select behaves like any other cell. Up/Down fall back to opening
      // when there's nowhere to travel (e.g. a lone select). A read-only /
      // disabled select can't open but still hands focus on to its neighbours.
      if (!inert && !e.ctrlKey && !e.metaKey && (e.key === 'Enter' || e.key === ' ' || e.key === 'F4'
        || (e.altKey && (e.key === 'ArrowDown' || e.key === 'ArrowUp')))) {
        e.preventDefault();
        setOpen(true);
        return;
      }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        moveFocusHorizontal(e.currentTarget, e.key === 'ArrowLeft' ? 'left' : 'right');
        return;
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        if (!moveFocusVertical(e.currentTarget, e.key === 'ArrowUp' ? 'up' : 'down') && !inert) setOpen(true);
        return;
      }
      // A printable character on a filterable closed select opens the list
      // with the filter seeded — type-ahead straight from the field.
      if (!inert && filterable && e.key.length === 1 && e.key !== ' ' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        seedRef.current = e.key;
        setOpen(true);
        return;
      }
      return;
    }
    if (inert) return;
    // When open, arrows move highlight, Enter picks.
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => Math.min(h + 1, shown.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
    else if (e.key === 'Home') { e.preventDefault(); setHighlight(0); }
    else if (e.key === 'End') { e.preventDefault(); setHighlight(shown.length - 1); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = shown[highlight];
      if (opt) pick(opt);
    }
    // A second Space closes the list (toggle), keeping focus on the field.
    else if (e.key === ' ') { e.preventDefault(); setOpen(false); }
    // Left/Right close the list and travel to the neighbouring field rather
    // than leaving the open list stranded.
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      setOpen(false);
      moveFocusHorizontal(e.currentTarget, e.key === 'ArrowLeft' ? 'left' : 'right');
    }
  }

  // One structure, drill or not: a wrapper box (border, background, focus ring)
  // holds the trigger button and, beside it, the drill icon link and the chevron,
  // so nothing interactive sits inside the button. With a drill-in, disabled and
  // read-only are the same grey box around the same focusable, labelled combobox
  // (aria-disabled / aria-readonly; no native `disabled`, which would swallow the
  // icon's clicks). Without one they keep the old dimmed look.
  const nativeDisabled = !!disabled && !canOpen;
  const boxTone = inert && hasDrill ? 'bg-slate-50 border-border' : `bg-surface ${isInvalid ? 'border-red-500' : tone === 'warning' ? 'border-rose-300' : 'border-controlBorder'}`;
  return (
    <div ref={rootRef} className="relative w-full">
      <div data-picker-box="" title={closedLabel != null && selected ? selected.label : undefined} className={`w-full min-h-[26px] px-2.5 rounded-md border text-[0.8125rem] transition-colors flex items-center gap-2 ${inert && hasDrill ? DRILL_BOX_INERT_FOCUS : tone === 'warning' && !isInvalid ? 'focus-within:border-rose-400' : DRILL_BOX_FOCUS} ${boxTone} ${isInvalid ? 'focus-within:border-red-500' : ''} ${inert ? (hasDrill ? 'cursor-not-allowed' : 'opacity-50 cursor-not-allowed') : 'cursor-default'} ${className}`}>
      <button
        {...rest}
        {...aria}
        ref={btnRef}
        type="button"
        disabled={nativeDisabled}
        aria-disabled={disabled && !nativeDisabled ? true : undefined}
        aria-readonly={readOnly || undefined}
        aria-keyshortcuts={canOpen ? 'Alt+Enter' : undefined}
        // A travel stop for arrow-key field navigation (spatialNav targets
        // [data-travel]): the arrows can land on, and move off, a select.
        data-travel={nativeDisabled ? undefined : ''}
        onKeyDown={onTriggerKeyDown}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open && !inert ? listId : undefined}
        aria-activedescendant={open && !inert && !filterable && shown[highlight] ? optionId(highlight) : undefined}
        // Click on the field body is a no-op — the user has to click the
        // arrow (or use the keyboard) to open the list. Matches the ask
        // in the UX spec. Stay as a normal focusable button otherwise.
        onClick={(e) => {
          e.preventDefault();
          if (roomy && !inert) setOpen((o) => !o);
        }}
        className={`${DRILL_TRIGGER} justify-between cursor-[inherit]`}
      >
        <span className={`flex-1 truncate ${selected ? 'text-primary-text' : 'text-muted'}`}>
          {selected ? (closedLabel ?? selected.label) : ''}
        </span>
      </button>
      {(canOpen || alwaysDrill) && <DrillAffordance openTo={openTo} openLabel={openLabel} active={canOpen} onOpen={openRecord} />}
      {!(inert && hasDrill) && <span
        role="button"
        tabIndex={-1}
        aria-label={open ? 'Close options' : 'Open options'}
        onMouseDown={(e) => {
          // stopPropagation so the outer click() preventDefault doesn't
          // swallow our toggle. Toggle on mousedown (not click) so the
          // focus-outside handler doesn't close it in the same tick.
          e.preventDefault();
          e.stopPropagation();
          if (!inert) {
            setOpen((o) => !o);
            // Keep focus on the trigger button, not this span.
            btnRef.current?.focus();
          }
        }}
        className={`shrink-0 inline-flex items-center justify-center w-[24px] h-[24px] -mr-[4px] rounded-md text-secondary hover:text-primary-text hover:bg-slate-100 cursor-pointer select-none transition-transform ${open ? 'rotate-180' : ''}`}
      >
        <IconChevron size={14} />
      </span>}
      </div>
      {open && !inert && menuStyle && createPortal(
        <div
          ref={listRef}
          // Tag it so the global table-arrow-nav hook doesn't steal arrow
          // keys while the list is open.
          data-grid-nav-skip
          className="animate-dropdown z-popover bg-surface rounded-md border border-border shadow-lg overflow-y-auto"
          style={menuStyle}
        >
          {filterable && (
            <div className="sticky top-0 bg-surface border-b border-border-light p-1">
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => Math.min(h + 1, shown.length - 1)); }
                  else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
                  else if (e.key === 'Enter') { e.preventDefault(); const opt = shown[highlight]; if (opt) pick(opt); }
                  else if (e.key === 'Escape') { e.preventDefault(); setOpen(false); btnRef.current?.focus(); }
                  e.stopPropagation();
                }}
                placeholder="Type to filter…"
                aria-label="Filter options"
                role="combobox"
                aria-expanded={true}
                aria-controls={listId}
                aria-autocomplete="list"
                aria-activedescendant={shown[highlight] ? optionId(highlight) : undefined}
                data-grid-nav-skip
                className="w-full h-6 px-2 rounded border border-border bg-surface text-[0.75rem] outline-none focus:border-primary"
              />
            </div>
          )}
          {shown.length === 0 && (
            <div role="status" className="px-3 py-2 text-[0.75rem] text-muted">{query.trim() ? 'No matches' : 'No options'}</div>
          )}
          <div role="listbox" id={listId}>
            {shown.map((o, i) => (
              <div
                key={`${o.value}-${i}`}
                id={optionId(i)}
                data-idx={i}
                role="option"
                aria-selected={o.value === currentValue}
                aria-disabled={o.disabled || undefined}
                onMouseEnter={() => setHighlight(i)}
                onMouseDown={(e) => { e.preventDefault(); pick(o); }}
                className={`px-3 ${roomy ? 'py-2.5 text-[0.9375rem]' : 'py-1.5 text-[0.8125rem]'} truncate ${
                  o.disabled
                    ? 'text-muted cursor-not-allowed'
                    : i === highlight
                      ? 'bg-primary-light text-primary-text cursor-pointer'
                      : 'text-primary-text cursor-pointer'
                } ${o.value === currentValue ? 'font-semibold' : ''}`}
              >
                {o.label}
              </div>
            ))}
          </div>
        </div>,
        document.documentElement,
      )}
    </div>
  );
}
